import type { ReactNode } from "react";

/**
 * The left menu, read off the route tree.
 *
 * A hand-written array of links is two lists that have to agree: add a route
 * and the menu is silently short of it, delete one and the menu points at a
 * path that answers 404. Neither fails anything. So the items are derived —
 * a route says it belongs in the sidebar and that is the only place it is
 * said — which is the part of the fleet's layout worth taking literally.
 *
 * The derivation is kept apart from the component so it can be tested without
 * a router: everything here is a pure function of what the routes declare.
 */

/** What a route may declare about its place in the menu. */
export interface SidebarStaticData {
  title?: ReactNode;
  icon?: ReactNode;
  showInSidebar?: boolean;
  sidebarOrder?: number;
  requiredRole?: "owner" | "member";
  /**
   * The route this one sits under, for screens whose path does not say so.
   *
   * Ancestry is read from the paths wherever the paths tell the truth. A site
   * is addressed without its workspace, so its page is not under the list it
   * came from by path — and the trail on the screen where a trail matters most
   * would be a single word. This is that one missing edge, named as a route id
   * so a test can check it resolves; a path here would be a second spelling of
   * something the tree already holds.
   */
  parent?: string;
}

/** One entry, ready to render. */
export interface SidebarItem {
  key: string;
  /*
   * `url` and `children`, and not names of this console's choosing: the menu is
   * drawn by the fleet's `LayoutSidebarContent`, copied as it stands, and this
   * is the shape it reads (`useDynamicSidebar`'s `SidebarItem`). There are no
   * nested rows here yet — `children` is always empty — but the renderer walks
   * it, so it is part of the contract rather than a field to add later.
   */
  url: string;
  title: ReactNode;
  icon?: ReactNode;
  requiredRole?: "owner" | "member";
  children: SidebarItem[];
}

/** A route as this needs to see it: its path, and what it declared. */
export interface DeclaredRoute {
  id: string;
  to: string;
  staticData?: SidebarStaticData;
}

/**
 * Items in menu order.
 *
 * A route that asks to be shown without a title is dropped rather than drawn
 * blank — a nameless row is a target nobody can read, and the alternative
 * (falling back to the path) would put `/workspaces/$workspaceId/sites` in the
 * menu and look deliberate.
 *
 * Ordering is `sidebarOrder`, then the declaration order of the routes; a route
 * with no order sorts after those that have one, so adding an item without
 * thinking about position cannot push an existing one around.
 */
export function sidebarItems(routes: DeclaredRoute[]): SidebarItem[] {
  return routes
    .map((route, index) => ({ route, index }))
    .filter(({ route }) => route.staticData?.showInSidebar && route.staticData.title != null)
    .sort((a, b) => {
      const order = (entry: { route: DeclaredRoute }) =>
        entry.route.staticData?.sidebarOrder ?? Number.MAX_SAFE_INTEGER;
      return order(a) - order(b) || a.index - b.index;
    })
    .map(({ route }) => ({
      key: route.id,
      url: route.to,
      title: route.staticData?.title,
      icon: route.staticData?.icon,
      requiredRole: route.staticData?.requiredRole,
      children: [],
    }));
}

/**
 * The items this account may be shown, which is **not** an authorization
 * decision. Hiding a row saves somebody a click into a refusal; the API decides
 * what may happen, and the role itself arrives as client data (`/v1/me`), so
 * reading it as a gate would be trusting the answer to the question.
 */
export function visibleTo(items: SidebarItem[], role: string): SidebarItem[] {
  // `role` is whatever `/v1/me` said, not a checked union: it arrives as client
  // data, and treating it as one would be the type system agreeing with the
  // answer rather than with the question. An owner sees a member's rows too.
  return items.filter(
    (item) => !item.requiredRole || item.requiredRole === role || role === "owner",
  );
}

/**
 * The parameters a path needs, in the order it names them.
 *
 * Used to leave out an item the current page cannot address: a workspace-scoped
 * route has nothing to point at while nobody is in a workspace, and a link to
 * `/workspaces/$workspaceId/sites` with the placeholder left in it is a 404
 * dressed as navigation.
 */
function parametersOf(path: string): string[] {
  return [...path.matchAll(/\$([A-Za-z0-9_]+)/g)].map(([, name]) => name);
}

/** The item with its parameters filled in, or `null` when they cannot be. */
export function addressed(item: SidebarItem, params: Record<string, string | undefined>): SidebarItem | null {
  const url = fill(item.url, params);
  return url === null ? null : { ...item, url };
}

/**
 * The routes as declared, flattened out of the tree.
 *
 * Reading the tree the router is built from — rather than a list maintained
 * beside it — is the whole point: a route added without a `staticData` entry
 * simply does not appear, and one that declares itself cannot be forgotten.
 */
export function declaredRoutes(route: RouteLike): DeclaredRoute[] {
  // `children` is typed `unknown` on a leaf route in TanStack's generics, so
  // the shape is checked here rather than asserted: a route tree that stops
  // being an array of routes should produce no menu, not a crash in a sidebar.
  const children = Array.isArray(route.children) ? (route.children as RouteLike[]) : [];

  return [
    { id: route.id, to: route.to, staticData: route.options?.staticData },
    ...children.flatMap(declaredRoutes),
  ];
}

/** As much of a TanStack route as the walk above touches. */
export interface RouteLike {
  id: string;
  to: string;
  options?: { staticData?: SidebarStaticData };
  children?: unknown;
}

/**
 * The trail to the current screen, for the header's breadcrumbs.
 *
 * Ancestry is read from the paths themselves — a route whose path is a prefix
 * of this one is above it — rather than from a `parent` written on each route.
 * That keeps it the same kind of statement as the menu: the console can only
 * claim a step somebody could have taken, because the step *is* a route.
 *
 * The last crumb is the current screen and is not a link. A screen may hand in
 * its own name for it (`title`), which is how a site's detail says the site's
 * name — the route knows it is a site, only the screen knows which one.
 *
 * Which route you are on is asked by **id**, not by path. A path does not
 * identify a route: `/` is answered by three of them here — the root, the
 * pathless layout that holds the session check, and the home screen — and they
 * all carry `to: "/"`. Matching by path found the first of those, which names
 * nothing, and the header then drew no trail at all on the console's first
 * screen (review of #10).
 *
 * @param routes every declared route, from `declaredRoutes`
 * @param routeId the id of the route being rendered
 * @param params what the address holds, for filling placeholders in
 * @param title what the current screen calls itself, if it knows better
 */
export function breadcrumbTrail(
  routes: DeclaredRoute[],
  routeId: string,
  params: Record<string, string | undefined>,
  title?: string,
): { key: string; title: ReactNode; to: string | null }[] {
  const here = routes.find((route) => route.id === routeId);

  /*
   * The chain, walked rather than sorted.
   *
   * Ordering by path length was a stand-in for depth, and it held while every
   * trail was one line of descent — which stopped being true the moment a route
   * could declare an edge that joins two. Walking up gives the order by
   * construction, and `seen` both removes a route reached twice (a declared
   * edge to a route that is also a prefix ancestor) and stops a cycle, which is
   * otherwise a hang rather than a wrong picture.
   */
  const chain: DeclaredRoute[] = [];
  const seen = new Set<string>();

  for (let step = here; step && !seen.has(step.id); step = stepUp(routes, step)) {
    seen.add(step.id);
    chain.unshift(step);
  }

  const crumbs = chain
    .filter((route) => route.staticData?.title != null)
    .map((route) => {
      const addressed = fill(route.to, params);
      return addressed === null
        ? null
        : { key: route.id, title: route.staticData?.title as ReactNode, to: addressed };
    })
    .filter((crumb): crumb is NonNullable<typeof crumb> => crumb !== null);

  // The screen's own name replaces the route's for the page you are on, and
  // stands alone on a screen no route named.
  if (title && crumbs.at(-1)?.key === here?.id) crumbs.pop();

  return [
    ...crumbs.map((crumb, index) => ({
      ...crumb,
      to: index === crumbs.length - 1 && !title ? null : crumb.to,
    })),
    ...(title ? [{ key: `${routeId}:title`, title, to: null }] : []),
  ];
}

/**
 * The route above this one: the edge it declares, or else the nearest route
 * whose path it extends. Declared first, because a route only declares one when
 * the paths cannot say.
 */
function stepUp(routes: DeclaredRoute[], route: DeclaredRoute): DeclaredRoute | undefined {
  const declared = route.staticData?.parent;
  if (declared) return routes.find((each) => each.id === declared);

  return routes
    .filter((each) => each.staticData?.title != null)
    .filter((each) => each.to !== route.to && isAncestor(each.to, route.to))
    .sort((a, b) => b.to.length - a.to.length)[0];
}

/** Is `candidate` a path segment prefix of `path`? */
function isAncestor(candidate: string, path: string): boolean {
  if (candidate === "/") return false;
  return path === candidate || path.startsWith(`${candidate}/`);
}

/** The path with its placeholders filled, or `null` when they cannot be. */
function fill(path: string, params: Record<string, string | undefined>): string | null {
  const needed = parametersOf(path);
  if (needed.some((name) => !params[name])) return null;

  return needed.reduce((filled, name) => filled.replace(`$${name}`, params[name] as string), path);
}
